/**
 * Logging types for structured log entries.
 */

import type { JsonArray, JsonObject, JsonValue } from './common.types';

export type LogSeverity =
    | 'DEFAULT'
    | 'DEBUG'
    | 'INFO'
    | 'NOTICE'
    | 'WARNING'
    | 'ERROR'
    | 'CRITICAL'
    | 'ALERT'
    | 'EMERGENCY';

export type LogService =
    | 'pillar-backend'
    | 'pillar-frontend'
    | 'samba-core'
    | 'samba-preload'
    | 'samba-website'
    | 'samba-sidebar';

export type LogTopic = string;

export interface LogException extends JsonObject {
    name: string;
    message: string;
    stack?: string;
    cause?: LogException;
}

export const FORBIDDEN_LOG_DATA_KEYS = [
    'password',
    'passphrase',
    'secret',
    'api_key',
    'apiKey',
    'authorization',
    'cookie',
    'set-cookie',
    'pan',
    'cvc',
    'cvv',
    'ssn',

    // Valor Paytech secret fields
    'appid',
    'appkey',
    'channel',
] as const;

export type ForbiddenLogDataKey = (typeof FORBIDDEN_LOG_DATA_KEYS)[number];

export type LogDataValue = JsonValue; //string | number | boolean | null | LogDataValue[] | LogDataUnsafe;

// Just used to ensure that LogDataValur is a subtype of JsonValue
export function _test(arg: LogDataValue): JsonValue {
    return arg;
}

export type LogDataUnsafe = {
    [key: string]: LogDataValue;
};

export type LogDataExcluded = {
    [K in ForbiddenLogDataKey]?: never;
};

export type LogData = LogDataUnsafe & LogDataExcluded;

export interface LogMeta {
    service: LogService;
    topics: LogTopic[];
    data?: LogData;
    exception?: LogException;
    subsystem?: string;
    workflow_id?: string;
    website_id?: string;
    correlation_id?: string;
    operation_id?: string;
    trace_id?: string;
}

export interface RequestLogEntry {
    // Timestamps
    client_timestamp?: string; // ISO-8601 (Device Time - Origin)

    // Context
    severity: LogSeverity;
    service: LogService;
    topics: LogTopic[];
    subsystem?: string; // e.g., 'samba-rpa', 'samba-sidebar', 'ipg', 'ipw'
    workflow_id?: string; // RPA workflow module identifier (if applicable)
    website_id?: string; // Website identifier for WebContentsView logs (if applicable)
    operation_id: string; // Client-generated hash
    correlation_id?: string; // Cross-system correlation for business workflows
    trace_id?: string; // GCP Trace ID (propagated via X-Cloud-Trace-Context)

    // Customer ID is not inherent to the logged-in session and must be provided
    // by the caller in a context-dependent way.
    customer_id?: string;

    // Payload
    message: JsonValue; // Human readable
    args?: JsonArray; // Structured context (Redacted) - validated by explicit schema
    exception?: LogException;
    stack_trace?: string;
}
export interface AuthenticatedLogEntry extends RequestLogEntry, JsonObject {
    // Timestamps
    timestamp: string; // ISO-8601 (Server Time - Ingestion)

    // Identity (Injected by Backend for Client logs)
    user_id?: string;
    role_assignment_id?: string;
    audit_id?: string;
    organization_id?: string;

    // Customer ID is not inherent to the logged-in session and must be provided
    // by the caller in a context-dependent way.
    customer_id?: string;
}

export interface LogIngestRequest {
    entries: RequestLogEntry[];
}

export interface LogQueryRequest {
    user_id?: string;
    audit_id?: string;
    role_assignment_id?: string;
    organization_id?: string;
    customer_id?: string;
    trace_id?: string;
    from_timestamp?: string; // ISO-8601
    to_timestamp?: string; // ISO-8601
    limit?: number;
}

export interface LogQueryResponse {
    entries: AuthenticatedLogEntry[];
}

export const COMPLIANCE_LOG_TOPIC: LogTopic = 'COMPLIANCE';
